import type { CompleteMessage } from "@elaso/shared/types/db";
import {
	ArrowDown,
	ChevronRight,
	EyeOff,
	Hash,
	MessageSquare,
	MoreVertical,
	Pencil,
	Reply,
	RotateCcw,
	Smile,
	Trash2,
} from "lucide-react";
import {
	useCallback,
	useEffect,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { useStore } from "zustand";
import { ProfileOrganization } from "@/components/app/profile-organization";
import { AvatarWithStatus } from "@/components/ui/avatar-with-status";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { FileAttachmentList } from "@/components/ui/file-attachment";
import {
	HoverCard,
	HoverCardContent,
	HoverCardTrigger,
} from "@/components/ui/hover-card";
import { MessageEmbeds } from "@/components/ui/message-embeds";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/components/ui/popover";
import { RichTextRenderer } from "@/components/ui/rich-text-renderer";
import { useSelfPermissionsOnChannel } from "@/hooks/use-accessible-channels";
import { useUser } from "@/hooks/use-user";
import { authStore } from "@/lib/stores/auth-store";
import { coreStore } from "@/lib/stores/core-store";
import { uiStore } from "@/lib/stores/ui-store";
import { cn } from "@/lib/utils";
import { formatTime } from "@/utils/date";
import { EmojiPicker } from "../ui/emoji-picker";
import { Image } from "../ui/image";
import { TiptapEditor } from "../ui/tiptap-editor";

const DIVIDER_DISPLAY_DURATION_MS = 10_000;
const SCROLL_THRESHOLD = 150;
const LOAD_MORE_THRESHOLD = 100;
const HIGHLIGHT_DURATION_MS = 2000;

const CUSTOM_EMOJI_REGEX = /<img\s+src="([^"]+)"\s+alt="([^"]*)"/;

function renderEmoji(emoji: string) {
	const imgMatch = emoji.match(CUSTOM_EMOJI_REGEX);
	if (imgMatch) {
		return (
			<Image
				alt={imgMatch[2]}
				className="inline-emoji size-5"
				height={20}
				src={imgMatch[1]}
				width={20}
			/>
		);
	}
	return emoji;
}

type ScrollState = {
	isAtBottom: boolean;
	showScrollButton: boolean;
};

function useAutoScroll(
	containerRef: React.RefObject<HTMLDivElement | null>,
	messages: CompleteMessage[],
	currentUserId: string | undefined
) {
	const [scrollState, setScrollState] = useState<ScrollState>({
		isAtBottom: true,
		showScrollButton: false,
	});

	const prevScrollHeightRef = useRef(0);
	const prevFirstMessageIdRef = useRef<string | null>(null);
	const userScrolledRef = useRef(false);
	const suppressAutoScrollRef = useRef(false);
	const lastMessageFromCurrentUserRef = useRef(false);

	const scrollToBottom = useCallback(
		(behavior: ScrollBehavior = "instant") => {
			const container = containerRef.current;
			if (!container) return;
			container.scrollTo({ top: container.scrollHeight, behavior });
			userScrolledRef.current = false;
			setScrollState({ isAtBottom: true, showScrollButton: false });
		},
		[containerRef]
	);

	const handleScroll = useCallback(() => {
		const container = containerRef.current;
		if (!container) return;

		const { scrollTop, scrollHeight, clientHeight } = container;
		const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
		const isAtBottom = distanceFromBottom < SCROLL_THRESHOLD;

		if (!isAtBottom) {
			userScrolledRef.current = true;
		}

		const showScrollButton = distanceFromBottom > SCROLL_THRESHOLD * 2;

		// Bail out when neither value changed. handleScroll fires on every scroll
		// event, and without this each tick allocated a new state object and forced
		// a full MessageList re-render, reconciling every message row, which was the
		// main cause of scroll jank in busy channels. Returning the previous state
		// lets React skip the render entirely.
		setScrollState((prev) =>
			prev.isAtBottom === isAtBottom &&
			prev.showScrollButton === showScrollButton
				? prev
				: { isAtBottom, showScrollButton }
		);
	}, [containerRef]);

	useEffect(() => {
		const container = containerRef.current;
		if (!container) return;

		const observer = new ResizeObserver(() => {
			const currentFirstId = messages[0]?.id ?? null;
			const prevFirstId = prevFirstMessageIdRef.current;
			const newScrollHeight = container.scrollHeight;

			if (suppressAutoScrollRef.current) {
				// Bookkeeping only, leaving the scroll position alone.
				prevScrollHeightRef.current = newScrollHeight;
				prevFirstMessageIdRef.current = currentFirstId;
				suppressAutoScrollRef.current = false;
				return;
			}

			// A changed first message means history loaded, so hold the position.
			if (prevFirstId && currentFirstId !== prevFirstId) {
				const diff = newScrollHeight - prevScrollHeightRef.current;
				if (diff > 0) {
					container.scrollTop += diff;
				}
			}
			// The caller sent the new message, so always scroll.
			else if (lastMessageFromCurrentUserRef.current) {
				scrollToBottom();
				lastMessageFromCurrentUserRef.current = false;
			}
			// Nobody has scrolled up, so keep following the new content.
			else if (!userScrolledRef.current) {
				scrollToBottom();
			}

			prevScrollHeightRef.current = newScrollHeight;
			prevFirstMessageIdRef.current = currentFirstId;
		});

		// The first child is the content wrapper, which is what changes height.
		const content = container.firstElementChild;
		if (content) {
			observer.observe(content);
		}

		return () => observer.disconnect();
	}, [containerRef, messages, scrollToBottom]);

	useEffect(() => {
		const lastMessage = messages.at(-1);
		if (lastMessage?.authorId === currentUserId && lastMessage?.isPending) {
			lastMessageFromCurrentUserRef.current = true;
		}
	}, [messages, currentUserId]);

	const resetScroll = useCallback(() => {
		userScrolledRef.current = false;
		suppressAutoScrollRef.current = false;
		prevScrollHeightRef.current = 0;
		prevFirstMessageIdRef.current = null;
		setScrollState({ isAtBottom: true, showScrollButton: false });
	}, []);

	const suppressAutoScroll = useCallback(() => {
		suppressAutoScrollRef.current = true;
	}, []);

	const markUserScrolled = useCallback(() => {
		userScrolledRef.current = true;
	}, []);

	return {
		...scrollState,
		scrollToBottom,
		handleScroll,
		resetScroll,
		suppressAutoScroll,
		markUserScrolled,
	};
}

type MessageListProps = {
	messages: CompleteMessage[];
	channelId: string;
	onReply?: (message: {
		id: string;
		authorId: string;
		richContent: Record<string, unknown>;
	}) => void;
	threadIds: Record<string, string>;
};

type RenderItem =
	| { type: "message"; data: CompleteMessage }
	| { type: "divider"; id: "new-messages-divider" };

export function MessageList({
	messages,
	channelId,
	onReply,
	threadIds,
}: MessageListProps) {
	const [highlightedMessageId, setHighlightedMessageId] = useState<
		string | null
	>(null);
	const [openEmojiPicker, setOpenEmojiPicker] = useState<string | null>(null);
	const [openInlineEmojiPicker, setOpenInlineEmojiPicker] = useState<
		string | null
	>(null);
	const [dividerMessageId, setDividerMessageId] = useState<string | null>(null);
	const [showDivider, setShowDivider] = useState(true);
	const [editingMessageId, setEditingMessageId] = useState<string | null>(null);
	const [deleteConfirmMessageId, setDeleteConfirmMessageId] = useState<
		string | null
	>(null);
	// Deleting a message that anchors a thread deletes the thread with it
	// (TECH-3169), so the confirmation has to say so first. The anchor is the only
	// way into a thread, so it cannot be a silent side effect.
	const deleteConfirmHasThread = deleteConfirmMessageId
		? Boolean(
				messages.find((m) => m.id === deleteConfirmMessageId)?.threadChannelId
			)
		: false;

	const messageRefs = useRef<Map<string, HTMLDivElement>>(new Map());
	const scrollContainerRef = useRef<HTMLDivElement>(null);
	const dividerRef = useRef<HTMLDivElement>(null);
	const isFetchingHistory = useRef(false);

	const user = useStore(authStore, (state) => state.user);
	const addReaction = useStore(coreStore, (state) => state.addReaction);
	const removeReaction = useStore(coreStore, (state) => state.removeReaction);
	const suppressEmbeds = useStore(coreStore, (state) => state.suppressEmbeds);
	const editMessage = useStore(coreStore, (state) => state.editMessage);
	const deleteMessage = useStore(coreStore, (state) => state.deleteMessage);
	const markChannelUnread = useStore(
		coreStore,
		(state) => state.markChannelUnread
	);
	const permissionLevel = useSelfPermissionsOnChannel(channelId);
	const canModerate = permissionLevel === "moderate";

	const setChannelViewOption = useStore(
		uiStore,
		(state) => state.setChannelViewOption
	);
	const fetchMessageHistory = useStore(
		coreStore,
		(state) => state.fetchMessageHistory
	);
	const paginationMetadata = useStore(
		coreStore,
		(state) => state.channels.get(channelId)?.pagination
	);
	const retryMessage = useStore(coreStore, (state) => state.retryMessage);
	const deleteOptimisticMessage = useStore(
		coreStore,
		(state) => state.deleteOptimisticMessage
	);

	const { getUserDisplayName } = useUser();
	const lastSeenMessageId = useStore(
		coreStore,
		(state) => state.channels.get(channelId)?.readStatus ?? null
	);
	const forcedUnreadMessageId = useStore(
		coreStore,
		(state) => state.channels.get(channelId)?.forcedUnreadMessageId ?? null
	);

	const {
		scrollToBottom,
		handleScroll,
		resetScroll,
		suppressAutoScroll,
		markUserScrolled,
		showScrollButton,
	} = useAutoScroll(scrollContainerRef, messages, user?.uid);

	const setDividerFromForcedUnread = useCallback(
		(msgId: string) => {
			const idx = messages.findIndex((m) => m.id === msgId);
			if (idx > 0) {
				setDividerMessageId(messages[idx - 1].id);
			} else if (idx === 0) {
				setDividerMessageId("__top__");
			} else {
				setDividerMessageId(lastSeenMessageId);
			}
		},
		[messages, lastSeenMessageId]
	);

	// Set divider position on mount and channel switch
	// biome-ignore lint/correctness/useExhaustiveDependencies: only re-run on channel change, values are derived from channelId
	useEffect(() => {
		if (forcedUnreadMessageId) {
			setDividerFromForcedUnread(forcedUnreadMessageId);
		} else {
			setDividerMessageId(lastSeenMessageId);
		}
		initialForcedRef.current = forcedUnreadMessageId;
	}, [channelId]);

	// React to forced-unread changes while the channel is open, such as a click on
	// "Mark as Unread".
	const initialForcedRef = useRef(forcedUnreadMessageId);
	useEffect(() => {
		// Skip the value already handled by the channel-change effect above
		if (initialForcedRef.current === forcedUnreadMessageId) return;
		initialForcedRef.current = forcedUnreadMessageId;

		if (forcedUnreadMessageId) {
			setDividerFromForcedUnread(forcedUnreadMessageId);
		}
	}, [forcedUnreadMessageId, setDividerFromForcedUnread]);

	useEffect(() => {
		if (!dividerMessageId) return;

		setShowDivider(true);
		const timer = setTimeout(() => {
			setShowDivider(false);
		}, DIVIDER_DISPLAY_DURATION_MS);
		return () => clearTimeout(timer);
	}, [dividerMessageId]);

	useEffect(() => {
		if (messages.length > 0) {
			const lastMessage = messages.at(-1);
			if (lastMessage?.isPending) {
				setDividerMessageId(null);
			}
		}
	}, [messages]);

	useEffect(() => {
		resetScroll();
		isFetchingHistory.current = false;
		pendingDividerScroll.current = true;
	}, [channelId, resetScroll]);

	const pendingDividerScroll = useRef(true);

	const itemsToRender = useMemo(() => {
		const items: RenderItem[] = [];

		if (!messages.length) return items;

		if (dividerMessageId === "__top__") {
			items.push({ type: "divider", id: "new-messages-divider" });
		}

		for (let i = 0; i < messages.length; i++) {
			const message = messages[i];
			items.push({ type: "message", data: message });

			if (message.id === dividerMessageId && i < messages.length - 1) {
				items.push({ type: "divider", id: "new-messages-divider" });
			}
		}
		return items;
	}, [messages, dividerMessageId]);

	// On channel open, scroll to the divider if there is one, otherwise leave it
	// to auto-scroll.
	useLayoutEffect(() => {
		if (!pendingDividerScroll.current) return;
		if (!messages.length) return;

		if (dividerRef.current) {
			pendingDividerScroll.current = false;
			suppressAutoScroll();
			dividerRef.current.scrollIntoView({
				block: "center",
				behavior: "instant",
			});
			// Stops the ResizeObserver overriding this position when images and
			// embeds load and change the content height.
			markUserScrolled();
		} else if (!dividerMessageId) {
			// No divider, so the existing auto-scroll handles scroll-to-bottom.
			pendingDividerScroll.current = false;
		} else if (
			dividerMessageId !== "__top__" &&
			!messages.some((m) => m.id === dividerMessageId)
		) {
			// The divider target is not among the loaded messages, too far back in
			// history, so fall back to lastSeenMessageId or give up.
			pendingDividerScroll.current = false;
			setDividerMessageId(null);
		}
	}, [
		itemsToRender,
		suppressAutoScroll,
		markUserScrolled,
		dividerMessageId,
		messages,
	]);

	const onScroll = useCallback(
		(e: React.UIEvent<HTMLDivElement>) => {
			handleScroll();

			const { scrollTop } = e.currentTarget;
			if (
				scrollTop < LOAD_MORE_THRESHOLD &&
				paginationMetadata?.hasMore &&
				!paginationMetadata?.isLoading &&
				paginationMetadata?.cursor &&
				!isFetchingHistory.current
			) {
				isFetchingHistory.current = true;
				fetchMessageHistory({
					channelId,
					cursor: paginationMetadata.cursor,
				}).finally(() => {
					isFetchingHistory.current = false;
				});
			}
		},
		[
			channelId,
			fetchMessageHistory,
			handleScroll,
			paginationMetadata?.cursor,
			paginationMetadata?.hasMore,
			paginationMetadata?.isLoading,
		]
	);

	const getDisplayName = (authorId: string) => getUserDisplayName(authorId);

	const getInitials = (authorId: string) => {
		const displayName = getDisplayName(authorId);
		return displayName.charAt(0).toUpperCase();
	};

	const scrollToMessage = (messageId: string) => {
		const element = messageRefs.current.get(messageId);
		if (element) {
			element.scrollIntoView({ behavior: "smooth", block: "center" });
			setHighlightedMessageId(messageId);
			setTimeout(() => setHighlightedMessageId(null), HIGHLIGHT_DURATION_MS);
		}
	};

	const handleEmojiSelect = (messageId: string, emoji: string) => {
		if (user?.uid) {
			addReaction({ messageId, emoji });
			setOpenEmojiPicker(null);
			setOpenInlineEmojiPicker(null);
		}
	};

	const handleReactionClick = (messageId: string, emoji: string) => {
		if (!user?.uid) return;

		const message = messages.find((m) => m.id === messageId);
		const userReaction = message?.reactions?.find(
			(r) => r.emoji === emoji && r.authorId === user.uid
		);

		if (userReaction) {
			removeReaction({ messageId, emoji });
		} else {
			addReaction({ messageId, emoji });
		}
	};

	/**
	 * True for a reply whose target has been deleted. The server clears the
	 * pointer, which it has to or the target could not be deleted at all, and
	 * stamps `referencedMessageDeletedAt` so this stays distinguishable from a
	 * message that was never a reply (TECH-3169).
	 */
	const isReplyToDeleted = (message: {
		referencedMessageDeletedAt?: Date | string | null;
	}): boolean => Boolean(message.referencedMessageDeletedAt);

	const groupReactions = (
		reactions?: Array<{ emoji: string; authorId: string }>
	) => {
		if (!reactions) return [];

		const grouped = reactions.reduce(
			(acc, reaction) => {
				if (!acc[reaction.emoji]) {
					acc[reaction.emoji] = {
						emoji: reaction.emoji,
						authors: [],
						count: 0,
					};
				}
				acc[reaction.emoji].authors.push(reaction.authorId);
				acc[reaction.emoji].count += 1;
				return acc;
			},
			{} as Record<string, { emoji: string; authors: string[]; count: number }>
		);

		return Object.values(grouped);
	};

	return (
		<div className="relative shrink grow overflow-hidden">
			<div
				className="scrollbar-thin scrollbar-thumb-muted-foreground/20 h-full overflow-y-auto overflow-x-hidden px-4 pt-4"
				onScroll={onScroll}
				ref={scrollContainerRef}
				style={{ overflowAnchor: "none" }}
			>
				<div className="flex flex-col gap-2 pb-4">
					{paginationMetadata?.isLoading && messages.length > 0 && (
						<div className="fade-in zoom-in flex animate-in justify-center py-4 duration-300">
							<span className="flex items-center gap-2 text-muted-foreground text-xs">
								{/** biome-ignore lint/a11y/noSvgWithoutTitle: <explanation> */}
								<svg className="h-3 w-3 animate-spin" viewBox="0 0 24 24">
									<circle
										className="opacity-25"
										cx="12"
										cy="12"
										fill="none"
										r="10"
										stroke="currentColor"
										strokeWidth="4"
									/>
									<path
										className="opacity-75"
										d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
										fill="currentColor"
									/>
								</svg>
								Loading history...
							</span>
						</div>
					)}

					{messages.length === 0 ? (
						<div className="flex h-[50vh] items-center justify-center text-muted-foreground">
							<p>No messages yet. Start the conversation!</p>
						</div>
					) : (
						itemsToRender.map((item) => {
							if (item.type === "divider") {
								if (!showDivider) return null;
								return (
									<div
										className="relative flex items-center justify-center py-2"
										key="new-messages-divider"
										ref={dividerRef}
									>
										<div className="absolute inset-0 flex items-center">
											<span className="w-full border-red-500/50 border-t" />
										</div>
										<div className="relative flex justify-center text-xs uppercase">
											<span className="bg-background px-2 font-semibold text-red-500">
												New Messages
											</span>
										</div>
									</div>
								);
							}

							const message = item.data;

							if (message.type === "system") {
								const metadata = message.metadata as any;
								if (metadata?.type === "thread_created") {
									return (
										<div
											className={cn(
												"relative",
												threadIds[message.id] && "mb-8"
											)}
											key={message.id}
										>
											<div className="group relative flex items-center gap-3 rounded pt-2 pr-4 pb-1 pl-6">
												<Hash className="size-4 text-muted-foreground" />
												<div className="flex items-baseline gap-2">
													<span className="text-sm">
														<span className="font-semibold">
															{getDisplayName(message.authorId)}
														</span>{" "}
														<span>created a new thread</span>
													</span>
													<span className="text-muted-foreground text-xs">
														{formatTime(message.createdAt.toString())}
													</span>
												</div>
											</div>
											{threadIds[message.id] && (
												<>
													<div className="flex w-9 items-end justify-start">
														<div
															className={cn(
																"-bottom-6 absolute top-12.5 left-7.75 w-6 rounded-bl-md border-muted-foreground/40 border-b-2 border-l-2",
																message.type === "system" && "top-8.5"
															)}
														/>
													</div>
													<Button
														className="-bottom-9 absolute left-16 flex h-auto w-full max-w-md cursor-pointer items-center justify-start gap-1.5 py-1 text-left text-muted-foreground text-xs transition-opacity hover:opacity-80 has-[>svg]:pr-2 has-[>svg]:pl-0.5"
														onClick={() => {
															setChannelViewOption(channelId, (prev) => ({
																...prev,
																sidePanel: {
																	type: "thread",
																	threadId: threadIds[message.id],
																},
															}));
														}}
														variant="link"
													>
														Open thread <ChevronRight />
													</Button>
												</>
											)}
										</div>
									);
								}
								return null;
							}

							return (
								<div
									className={cn(
										"relative",
										(message.referencedMessage || isReplyToDeleted(message)) &&
											"mt-6",
										threadIds[message.id] && "mb-8"
									)}
									key={message.id}
								>
									{/* The target was deleted, so there is nothing to quote or
									 * jump to, and the reply still has to read as a reply.
									 * Dropping the header would turn it into a standalone remark,
									 * which can invert what it meant (TECH-3169). Same shape as a
									 * real quote, minus the link. */}
									{!message.referencedMessage && isReplyToDeleted(message) && (
										<>
											<div className="-top-3 absolute left-7.75 mb-0.5 h-3 w-6 rounded-tl-md border-muted-foreground/40 border-t-2 border-l-2" />
											<div className="-top-4.5 absolute left-16 mb-0.5 flex items-center gap-1.5 text-left text-muted-foreground text-xs italic opacity-70">
												Original message was deleted
											</div>
										</>
									)}
									{message.referencedMessage && (
										<>
											<div className="-top-3 absolute left-7.75 mb-0.5 h-3 w-6 rounded-tl-md border-muted-foreground/40 border-t-2 border-l-2" />
											<button
												className="-top-4.5 absolute left-16 mb-0.5 flex cursor-pointer items-center gap-1.5 text-left text-muted-foreground text-xs transition-opacity hover:opacity-80"
												onClick={() =>
													message.referencedMessage &&
													scrollToMessage(message.referencedMessage.id)
												}
												title="Jump to message"
												type="button"
											>
												<AvatarWithStatus
													className="size-4"
													firebaseUid={message.referencedMessage.authorId}
													initials={getInitials(
														message.referencedMessage.authorId
													)}
													size="sm"
												/>
												<span className="font-semibold">
													{getDisplayName(message.referencedMessage.authorId)}
												</span>
												<ProfileOrganization
													className="shrink-0"
													uid={message.referencedMessage.authorId}
												/>
												<span className="line-clamp-1 opacity-80">
													{(message.referencedMessage.richContent as any)
														?.content?.[0]?.content?.[0]?.text || "[Message]"}
												</span>
											</button>
										</>
									)}

									<div
										className={cn(
											"group relative flex gap-3 rounded px-4 pt-2 pb-1 transition-colors duration-500",
											editingMessageId === message.id &&
												"bg-blue-50 dark:bg-blue-950/20",
											editingMessageId !== message.id &&
												highlightedMessageId === message.id &&
												"bg-yellow-100 dark:bg-yellow-900/20",
											editingMessageId !== message.id &&
												highlightedMessageId !== message.id &&
												"hover:bg-muted/50",
											message.isPending && "opacity-50",
											message.hasFailed &&
												"bg-red-50 opacity-70 dark:bg-red-950/20"
										)}
										ref={(el) => {
											if (el) messageRefs.current.set(message.id, el);
										}}
									>
										<AvatarWithStatus
											firebaseUid={message.authorId}
											initials={getInitials(message.authorId)}
											size="md"
										/>
										<div className="flex-1">
											<div className="flex items-baseline justify-between gap-2">
												<div className="flex items-baseline gap-2">
													<span className="font-semibold text-sm">
														{getDisplayName(message.authorId)}
													</span>
													<ProfileOrganization
														className="min-w-0"
														uid={message.authorId}
													/>
													<span className="text-muted-foreground text-xs">
														{formatTime(message.createdAt.toString())}
													</span>
													{message.isPending && (
														<span className="text-muted-foreground text-xs italic">
															{message.editedAt ? "Editing..." : "Sending..."}
														</span>
													)}
													{message.hasFailed && (
														<span className="text-red-500 text-xs italic">
															Failed to send
														</span>
													)}
												</div>

												{message.hasFailed ? (
													<div className="-top-4 absolute right-0 flex gap-1 rounded-md border bg-muted p-1">
														<Button
															className="size-7"
															onClick={() => retryMessage(message.id)}
															size="icon"
															title="Retry"
															variant="ghost"
														>
															<RotateCcw className="size-4" />
														</Button>
														<Button
															className="size-7"
															onClick={() =>
																deleteOptimisticMessage(message.id)
															}
															size="icon"
															title="Delete"
															variant="ghost"
														>
															<Trash2 className="size-4" />
														</Button>
													</div>
												) : (
													<div
														className={cn(
															"-top-4 absolute right-0 flex gap-1 rounded-md border bg-muted opacity-0 transition-opacity group-hover:opacity-100",
															openEmojiPicker === message.id && "opacity-100",
															message.isPending && "hidden"
														)}
													>
														<Popover
															onOpenChange={(open) =>
																setOpenEmojiPicker(open ? message.id : null)
															}
															open={openEmojiPicker === message.id}
														>
															<PopoverTrigger asChild>
																<Button
																	className="size-7"
																	size="icon"
																	title="Add reaction"
																	variant="ghost"
																>
																	<Smile className="size-4" />
																</Button>
															</PopoverTrigger>
															<PopoverContent className="w-auto border-0 p-0">
																<EmojiPicker
																	onEmojiSelect={(emoji) =>
																		handleEmojiSelect(message.id, emoji.emoji)
																	}
																/>
															</PopoverContent>
														</Popover>
														<Button
															className="size-7"
															onClick={() =>
																onReply?.({
																	id: message.id,
																	authorId: message.authorId,
																	richContent: message.richContent as Record<
																		string,
																		unknown
																	>,
																})
															}
															size="icon"
															title="Reply to message"
															variant="ghost"
														>
															<Reply className="size-4" />
														</Button>
														<DropdownMenu>
															<DropdownMenuTrigger asChild>
																<Button
																	className="size-7"
																	size="icon"
																	title="More actions"
																	variant="ghost"
																>
																	<MoreVertical className="size-4" />
																</Button>
															</DropdownMenuTrigger>
															<DropdownMenuContent align="end">
																{user?.uid === message.authorId && (
																	<DropdownMenuItem
																		onClick={() => {
																			setEditingMessageId(message.id);
																		}}
																	>
																		<Pencil className="size-4" />
																		Edit Message
																	</DropdownMenuItem>
																)}
																<DropdownMenuItem
																	onClick={() => {
																		setChannelViewOption(channelId, (prev) => ({
																			...prev,
																			sidePanel: {
																				type: "create-thread",
																				replyToMessageId: message.id,
																			},
																		}));
																	}}
																>
																	<MessageSquare className="size-4" />
																	Start Thread
																</DropdownMenuItem>
																<DropdownMenuItem
																	onClick={() => {
																		markChannelUnread(channelId, message.id);
																	}}
																>
																	<EyeOff className="size-4" />
																	Mark as Unread
																</DropdownMenuItem>
																{(user?.uid === message.authorId ||
																	canModerate) && (
																	<DropdownMenuItem
																		className="text-destructive focus:text-destructive"
																		onClick={(e) => {
																			if (e.shiftKey) {
																				deleteMessage({ id: message.id });
																			} else {
																				setDeleteConfirmMessageId(message.id);
																			}
																		}}
																	>
																		<Trash2 className="size-4" />
																		Delete Message
																	</DropdownMenuItem>
																)}
															</DropdownMenuContent>
														</DropdownMenu>
													</div>
												)}
											</div>

											<div className="mt-0.5">
												{editingMessageId === message.id ? (
													<div className="flex flex-col gap-1">
														<TiptapEditor
															autoFocus
															editorClassName="max-h-[200px] py-2"
															initialContent={
																message.richContent as Record<string, unknown>
															}
															onCancel={() => setEditingMessageId(null)}
															onSubmit={(_html, richContent) => {
																editMessage({
																	id: message.id,
																	richContent,
																});
																setEditingMessageId(null);
															}}
															placeholder="Edit message..."
															submitLabel="Save"
														/>
													</div>
												) : (
													<>
														<RichTextRenderer
															className="max-w-none"
															richContent={
																message.richContent as Record<string, unknown>
															}
														/>
														{message.editedAt && (
															<span className="text-muted-foreground text-xs">
																(edited)
															</span>
														)}
													</>
												)}

												{message.attachments &&
													message.attachments.length > 0 && (
														<FileAttachmentList
															className="mt-2"
															files={message.attachments}
														/>
													)}

												{editingMessageId !== message.id && (
													<MessageEmbeds
														embedData={(message as any).embedData}
														onSuppressEmbeds={() => {
															suppressEmbeds({
																messageId: message.id,
																suppress: true,
															});
														}}
														suppressedEmbeds={(message as any).suppressEmbeds}
													/>
												)}
											</div>

											{!(message.isPending || message.hasFailed) &&
												message.reactions &&
												message.reactions.length > 0 && (
													<div className="mt-2 flex flex-wrap items-center gap-1.5">
														{groupReactions(message.reactions).map(
															(reaction) => {
																const hasUserReacted =
																	user?.uid &&
																	reaction.authors.includes(user.uid);
																return (
																	<HoverCard key={reaction.emoji}>
																		<HoverCardTrigger asChild>
																			<Button
																				className={cn(
																					"relative items-center gap-1 overflow-hidden leading-0",
																					hasUserReacted &&
																						"border-primary bg-primary/10 hover:bg-primary/20 dark:bg-primary/10 dark:hover:bg-primary/20"
																				)}
																				onClick={(e) => {
																					e.stopPropagation();
																					handleReactionClick(
																						message.id,
																						reaction.emoji
																					);
																				}}
																				size="sm"
																				variant="outline"
																			>
																				{hasUserReacted && (
																					<span
																						aria-hidden="true"
																						className="absolute inset-0 flex items-center justify-center text-[4em] opacity-15 blur-md saturate-150"
																					>
																						{renderEmoji(reaction.emoji)}
																					</span>
																				)}
																				<span className="relative z-10 flex items-center gap-1">
																					{renderEmoji(reaction.emoji)}
																					<span className="font-medium text-xs">
																						{reaction.count}
																					</span>
																				</span>
																			</Button>
																		</HoverCardTrigger>
																		<HoverCardContent
																			className="w-auto max-w-xs p-2"
																			side="top"
																		>
																			<div className="scrollbar-thin scrollbar-thumb-muted-foreground/20 max-h-48 overflow-y-auto">
																				<div className="space-y-1">
																					{reaction.authors.map((authorId) => (
																						<div
																							className="rounded px-2 py-1 text-sm hover:bg-muted/50"
																							key={authorId}
																						>
																							{getUserDisplayName(authorId)}
																						</div>
																					))}
																				</div>
																			</div>
																		</HoverCardContent>
																	</HoverCard>
																);
															}
														)}
														<Popover
															onOpenChange={(open) =>
																setOpenInlineEmojiPicker(
																	open ? message.id : null
																)
															}
															open={openInlineEmojiPicker === message.id}
														>
															<PopoverTrigger asChild>
																<Button
																	className="gap-0.75"
																	size="sm"
																	title="Add reaction"
																	variant="outline"
																>
																	<Smile />
																</Button>
															</PopoverTrigger>
															<PopoverContent className="w-auto border-0 p-0">
																<EmojiPicker
																	onEmojiSelect={(emoji) =>
																		handleEmojiSelect(message.id, emoji.emoji)
																	}
																/>
															</PopoverContent>
														</Popover>
													</div>
												)}
										</div>
									</div>

									{threadIds[message.id] && (
										<>
											<div className="flex w-9 items-end justify-start">
												<div className="-bottom-6 absolute top-12.5 left-7.75 w-6 rounded-bl-md border-muted-foreground/40 border-b-2 border-l-2" />
											</div>
											<Button
												className="-bottom-9 absolute left-16 flex h-auto w-full max-w-md cursor-pointer items-center justify-start gap-1.5 py-1 text-left text-muted-foreground text-xs transition-opacity hover:opacity-80 has-[>svg]:pr-2 has-[>svg]:pl-0.5"
												onClick={() => {
													setChannelViewOption(channelId, (prev) => ({
														...prev,
														sidePanel: {
															type: "thread",
															threadId: threadIds[message.id],
														},
													}));
												}}
												variant="link"
											>
												Open thread <ChevronRight />
											</Button>
										</>
									)}
								</div>
							);
						})
					)}
				</div>
			</div>
			{showScrollButton && (
				<Button
					className="absolute right-6 bottom-4 z-10 rounded-full shadow-lg"
					onClick={() => scrollToBottom("smooth")}
					size="icon"
					variant="secondary"
				>
					<ArrowDown className="size-4" />
				</Button>
			)}
			<Dialog
				onOpenChange={(open) => {
					if (!open) setDeleteConfirmMessageId(null);
				}}
				open={deleteConfirmMessageId !== null}
			>
				<DialogContent showCloseButton={false}>
					<DialogHeader>
						<DialogTitle>Delete Message</DialogTitle>
						<DialogDescription>
							This action cannot be undone. Are you sure you want to delete this
							message?
							{deleteConfirmHasThread && (
								<>
									{" "}
									<strong>
										Its thread will be deleted too, including everyone else's
										replies in it.
									</strong>
								</>
							)}
						</DialogDescription>
					</DialogHeader>
					<DialogFooter>
						<Button
							onClick={() => setDeleteConfirmMessageId(null)}
							variant="outline"
						>
							Cancel
						</Button>
						<Button
							onClick={() => {
								if (deleteConfirmMessageId) {
									deleteMessage({ id: deleteConfirmMessageId });
								}
								setDeleteConfirmMessageId(null);
							}}
							variant="destructive"
						>
							Delete
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</div>
	);
}
